import React, { createContext, useEffect, useState } from "react";

import { GetSelfResponse } from "lib/dto";
import { useCookieWatcher, useStorageCompanyOverride } from "ui/hooks";
import { useSelfQuery } from "ui/queries";

type CurrentCompany = { id: string; name: string };

type AuthContextValue = {
  isLoggedIn: boolean;
  fetchingPermissions: boolean;
  user: GetSelfResponse | undefined;
  companyContext: {
    currentCompany: CurrentCompany | undefined;
    isCompanyOverridden: boolean;
    overrideCompany: (data: CurrentCompany) => void;
    resetOverrideCompany: () => void;
  };
};
export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

type AuthContextProviderProps = {
  children: React.ReactNode;
};
export const AuthContextProvider = (props: AuthContextProviderProps) => {
  const { children } = props;
  const isLoggedIn = useCookieWatcher("id_token");
  const selfQuery = useSelfQuery(isLoggedIn);

  const [storage, setStorage, clearStorage] = useStorageCompanyOverride();
  const [currentCompany, setCurrentCompany] = useState<CurrentCompany | undefined>(undefined);

  // On initial load check if /self company was overwritten
  useEffect(() => {
    if (storage) {
      setCurrentCompany(storage);
    } else {
      if (selfQuery.isSuccess) {
        const data = {
          id: selfQuery.data.companyId,
          name: selfQuery.data.companyName,
        };
        setCurrentCompany(data);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfQuery.isSuccess, selfQuery.data]);

  const resetOverrideCompany = () => {
    if (selfQuery.isSuccess) {
      setCurrentCompany({ id: selfQuery.data.companyId, name: selfQuery.data.companyName });
    } else {
      setCurrentCompany(undefined);
    }
    clearStorage();
  };

  const overrideCompany = (data: CurrentCompany) => {
    setCurrentCompany(data);
    setStorage(`${data.id},${data.name}`);
  };

  // Always reset company override when user is no longer logged in.
  useEffect(() => {
    if (!isLoggedIn) {
      resetOverrideCompany();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <AuthContext.Provider
      value={{
        user: selfQuery.data,
        isLoggedIn,
        fetchingPermissions: selfQuery.isFetching,
        companyContext: {
          currentCompany,
          isCompanyOverridden: !!storage?.id,
          overrideCompany,
          resetOverrideCompany,
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
